import axios from "axios";

// ✅ Environment-based API URL configuration
// const API_BASE_URL =
//   window.location.hostname === "localhost"
//     ? "https://omantelsip.omantel.om"
//     : process.env.REACT_APP_SERVER_URL;
// const API_BASE_URL = "https://quiz-survey.onrender.com";
// const API_BASE_URL = "https://185.177.116.176";
const API_BASE_URL = "https://omandigitalservices.online";
// const API_BASE_URL = "https://164.68.98.76";
// const API_BASE_URL = "https://omantelsip.omantel.om";
// const API_BASE_URL = "http://localhost:8082";

// ✅ Allowed hosts for validation
const ALLOWED_HOSTS = [
  "omantelsip.omantel.om",
  "localhost:3000",
  "omandigitalservices.online",
];

// ✅ Validate current host
const validateHost = () => {
  const currentHost = window.location.host;
  if (!ALLOWED_HOSTS.includes(currentHost)) {
    console.error(`[Security Warning] Untrusted host detected: ${currentHost}`);
    return false;
  }
  return true;
};

validateHost();

// ✅ Create Axios instance with proper configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// 🧩 Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const csrfToken = localStorage.getItem("csrfToken");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }

    config.headers["X-Request-ID"] = `${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  },
);

// 🧩 Response Interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.code === "ERR_NETWORK" ||
      error.message?.includes("Network Error")
    ) {
      console.error("Network error:", {
        url: error.config?.url,
        method: error.config?.method,
      });
      return Promise.reject({
        message: "Network error. Please check your internet connection.",
        originalError: error,
      });
    }

    if (error.message?.includes("CORS")) {
      console.error("CORS error - server configuration issue");
      return Promise.reject({
        message: "Unable to connect to server. Please contact support.",
        originalError: error,
      });
    }

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.warn("Unauthorized - authentication required");
          localStorage.removeItem("token");
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
          break;
        case 403:
          console.warn("Forbidden - insufficient permissions");
          break;
        case 404:
          console.warn("Resource not found:", error.config?.url);
          break;
        case 422:
          console.warn("Validation error:", data);
          break;
        case 429:
          console.warn("Rate limit exceeded");
          break;
        case 500:
        case 502:
        case 503:
          console.error("Server error - please try again later");
          break;
        default:
          console.warn("HTTP error:", status);
      }

      return Promise.reject({
        status,
        message: data?.message || error.message,
        data: data,
        originalError: error,
      });
    }

    if (error.code === "ECONNABORTED") {
      return Promise.reject({
        message: "Request timeout. Please try again.",
        originalError: error,
      });
    }

    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────────────────────
// 📡 SSE Service — mirrors Flutter SseService exactly
// ─────────────────────────────────────────────────────────────

const MAX_RETRY_DELAY = 30;

class SseService {
  // ─────────────────────────────────────────────
  // Fields
  // ─────────────────────────────────────────────
  _abortController = null;
  _reconnectTimer = null;

  _username = "";
  _token = "";
  _onMandatoryPush = null;
  _onAuthError = null;

  _isConnected = false;
  _shouldReconnect = false;
  _isManuallyDisconnected = false;

  _currentRetryDelay = 5;

  // SSE line-parser state
  _eventName = "";
  _eventData = "";

  // ─────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────

  /**
   * Open (or re-open) the SSE stream.
   * @param {string} username
   * @param {string} token  – Bearer token; call updateToken() anytime it rotates
   * @param {Function} onMandatoryPush  – called with the parsed message object
   * @param {Function} [onAuthError]    – called on 401 / 403
   */
  connect({ username, token, onMandatoryPush, onAuthError }) {
    // Reset all flags on explicit connect
    this._isManuallyDisconnected = false;
    this._shouldReconnect = true;
    this._currentRetryDelay = 5;

    this._username = username;
    this._token = token;
    this._onMandatoryPush = onMandatoryPush;
    this._onAuthError = onAuthError ?? null;

    this._openStream();
  }

  /** Intentional disconnect — prevents any further reconnect attempts. */
  disconnect() {
    this._isManuallyDisconnected = true;
    this._shouldReconnect = false;
    this._reconnectTimer && clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    this._cleanup();
    console.log("👋 SSE disconnected manually");
  }

  /** Call this whenever the auth token rotates without a full reconnect. */
  updateToken(newToken) {
    this._token = newToken;
  }

  get isConnected() {
    return this._isConnected;
  }

  // ─────────────────────────────────────────────
  // Core Connection
  // ─────────────────────────────────────────────

  async _openStream() {
    // Double-guard — block any path from reopening after logout
    if (!this._shouldReconnect || this._isManuallyDisconnected) {
      console.log("🛑 SSE open aborted — disconnected by user.");
      return;
    }

    this._cleanup();

    const url = `${API_BASE_URL}/api/user/sse/subscribe/${this._username}`;
    console.log("🔌 Connecting to SSE:", url);

    this._abortController = new AbortController();

    try {
      const response = await fetch(url, {
        method: "GET",
        signal: this._abortController.signal,
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
          Connection: "keep-alive",
          Authorization: `Bearer ${this._token}`,
        },
      });

      // ─── Status Code Handling ──────────────────
      if (response.status === 401 || response.status === 403) {
        console.log(
          `🔒 SSE auth error: HTTP ${response.status} — stopping reconnect`,
        );
        this._shouldReconnect = false;
        this._isManuallyDisconnected = true;
        this._cleanup();
        if (this._onAuthError) this._onAuthError();
        return;
      }

      if (response.status === 429) {
        console.log(
          `⚠️ SSE too many connections (429). Waiting ${MAX_RETRY_DELAY}s...`,
        );
        this._currentRetryDelay = MAX_RETRY_DELAY;
        this._scheduleReconnect();
        return;
      }

      if (response.status !== 200) {
        console.log(`❌ SSE connection failed: HTTP ${response.status}`);
        this._scheduleReconnect();
        return;
      }
      // ──────────────────────────────────────────

      console.log("✅ SSE connected successfully");
      this._isConnected = true;
      this._currentRetryDelay = 5; // reset backoff on success

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const pump = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log("🔌 SSE stream closed");
              if (!this._isManuallyDisconnected) {
                this._scheduleReconnect();
              }
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split("\n");

            for (const line of lines) {
              this._handleLine(line);
            }
          }
        } catch (err) {
          if (err.name === "AbortError") {
            console.log("🛑 SSE fetch aborted");
            return;
          }
          console.log("❌ SSE stream error:", err);
          if (!this._isManuallyDisconnected) {
            this._scheduleReconnect();
          }
        }
      };

      pump();
    } catch (err) {
      if (err.name === "AbortError") return;
      console.log("❌ SSE connection error:", err);
      if (!this._isManuallyDisconnected) {
        this._scheduleReconnect();
      }
    }
  }

  // ─────────────────────────────────────────────
  // SSE Line Parser
  // ─────────────────────────────────────────────

  _handleLine(line) {
    // Server keep-alive comment
    if (line.startsWith(":")) {
      console.log("💓 Keep-alive received");
      return;
    }

    // Empty line = end of event block
    if (line.trim() === "") {
      if (this._eventData) {
        this._dispatchEvent(this._eventName, this._eventData);
        this._eventName = "";
        this._eventData = "";
      }
      return;
    }

    if (line.startsWith("event:")) {
      this._eventName = line.substring(6).trim();
    } else if (line.startsWith("data:")) {
      this._eventData = line.substring(5).trim();
    }
  }

  // ─────────────────────────────────────────────
  // Event Dispatcher
  // ─────────────────────────────────────────────

  _dispatchEvent(name, data) {
    console.log(`📩 SSE event [${name}]:`, data);

    if (name === "connected") {
      console.log("🤝 Handshake received:", data);
      return;
    }

    if (name === "quiz-survey") {
      try {
        const parsed = JSON.parse(data);
        this._handleMessage(parsed);
      } catch (e) {
        console.log("❌ Failed to parse SSE data:", e);
      }
    }
  }

  // ─────────────────────────────────────────────
  // Message Handler
  // ─────────────────────────────────────────────

  _handleMessage(data) {
    console.log("📩 Received message:", data);

    const targetedUsers = data.targetedUsers;
    if (Array.isArray(targetedUsers) && targetedUsers.length > 0) {
      if (!targetedUsers.includes(this._username)) {
        console.log("⏭️ Message not for this user");
        return;
      }
    }

    if (data.isMandatory === true) {
      console.log("🚨 Mandatory quiz received!");
      if (this._onMandatoryPush) this._onMandatoryPush(data);
    } else {
      console.log("ℹ️ Non-mandatory quiz, ignoring");
    }
  }

  // ─────────────────────────────────────────────
  // Reconnect with Exponential Backoff
  // ─────────────────────────────────────────────

  _scheduleReconnect() {
    // Never reconnect if manually disconnected
    if (!this._shouldReconnect || this._isManuallyDisconnected) return;

    this._isConnected = false;
    this._cleanup();

    console.log(`🔁 Reconnecting in ${this._currentRetryDelay} seconds...`);

    this._reconnectTimer = setTimeout(() => {
      // Re-check both flags at fire time
      if (this._shouldReconnect && !this._isManuallyDisconnected) {
        this._openStream();
      }
    }, this._currentRetryDelay * 1000);

    // Exponential backoff, clamped to MAX_RETRY_DELAY
    this._currentRetryDelay = Math.min(
      this._currentRetryDelay * 2,
      MAX_RETRY_DELAY,
    );
  }

  // ─────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────

  _cleanup() {
    if (this._abortController) this._abortController.abort();
    this._abortController = null;
    this._isConnected = false;
  }
}

// Singleton — one SSE connection for the whole app
export const sseService = new SseService();

// ─────────────────────────────────────────────────────────────
// ✅ API methods
// ─────────────────────────────────────────────────────────────

const api = {
  // get all regions
  getAllRegions: async () => {
    return await apiClient.get("/api/user/regions");
  },

  /**
   * Fetch surveys based on user role
   * @param {string} userId - User ID
   * @param {boolean} isAdmin - Whether user is admin
   */
  getSurveys: async (userId, isAdmin) => {
    const endpoint = isAdmin
      ? "/api/user/quiz-survey"
      : `/api/user/quiz-survey?userId=${userId}`;
    return await apiClient.get(endpoint);
  },

  /**
   * Update survey status
   * @param {string} id - Survey ID
   * @param {boolean} status - Active status
   */
  putSurveyStatus: async (id, status) => {
    return await apiClient.put(`/api/admin/quiz-survey/${id}`, { status });
  },

  /**
   * Update multiple survey statuses
   * @param {string[]} ids - Array of survey IDs
   * @param {boolean} status - Active status
   */
  patchMultipleSurveyStatus: async (ids, status) => {
    return await Promise.all(
      ids.map((id) =>
        apiClient.put(`/api/admin/quiz-survey/${id}`, { status }),
      ),
    );
  },

  /**
   * Delete a survey
   * @param {string} id - Survey ID
   */
  deleteSurvey: async (id) => {
    return await apiClient.delete(`/api/admin/quiz-survey/${id}`);
  },

  /**
   * Create announcement
   * @param {Object} payload - Announcement data
   */
  announceResult: async (payload) => {
    return await apiClient.post("/api/admin/announcements", payload);
  },

  /**
   * Publish a new quiz/survey
   * @param {Object} model - Quiz/survey data
   */
  publishQuiz: async (model) => {
    return await apiClient.post("/api/admin/quiz-survey", model);
  },

  /**
   * Fetch user announcements
   * @param {string} userId - User ID
   */
  fetchAnnouncements: async (userId) => {
    return await apiClient.get("/api/user/announcements", {
      params: { userId },
    });
  },

  /**
   * Mark all announcements as read
   * @param {string} userId - User ID
   */
  markAnnouncementsAsRead: async (userId) => {
    return await apiClient.put("/api/user/announcements/mark-all-read", {
      userId,
    });
  },

  /**
   * Fetch all users (admin only)
   */
  fetchUsers: async () => {
    return await apiClient.get("/api/user");
  },

  /**
   * Fetch mandatory surveys for user
   * @param {string} userId - User ID
   */
  fetchMandatorySurvey: async (userId) => {
    return await apiClient.get(`/api/user/quiz-survey?userId=${userId}`);
  },

  /**
   * Fetch quiz leaderboard
   * @param {string} quizId - Quiz ID
   */
  fetchLeaderboard: async (quizId) => {
    return await apiClient.get(`/api/user/quiz-survey/summary/${quizId}`);
  },

  /**
   * Fetch results for quiz or survey
   * @param {string} type - 'quiz' or 'survey'
   * @param {string} id - Quiz/Survey ID
   * @param {string} userRole - User role
   * @param {string} userId - User ID
   */
  fetchResults: async (type, id, userRole, userId) => {
    if (type === "quiz") {
      return userRole === "ADMIN"
        ? await apiClient.get(`/api/user/admin-quiz-result/${id}`)
        : await apiClient.get(`/api/user/quiz-result/${id}?userId=${userId}`);
    } else {
      return userRole === "ADMIN"
        ? await apiClient.get(`api/user/admin-survey-result/${id}`)
        : await apiClient.get(`/api/user/survey-result/${id}?userId=${userId}`);
    }
  },

  /**
   * Close a survey
   * @param {string} id - Survey ID
   */
  closeSurvey: async (id) => {
    return await apiClient.put(`/api/admin/quiz-survey/${id}`, {
      status: false,
    });
  },

  /**
   * Reopen survey with extended duration
   * @param {string} id - Survey ID
   * @param {number} extendMinutes - Minutes to extend
   */
  reopenSurvey: async (id, extendMinutes = 10) => {
    const currentSurvey = await apiClient.get(`/api/user/quiz-survey/${id}`);
    const currentDuration = parseInt(
      currentSurvey.data.data.quizTotalDuration,
      10,
    );
    const newDuration = currentDuration + extendMinutes;

    return await apiClient.put(`/api/admin/quiz-survey/${id}`, {
      status: true,
      quizTotalDuration: newDuration.toString(),
    });
  },

  /**
   * Fetch survey by ID
   * @param {string} id - Survey ID
   */
  fetchSurveyById: async (id) => {
    return await apiClient.get(`/api/user/quiz-survey/${id}`);
  },

  /**
   * Submit survey response
   * @param {string} id - Survey ID
   * @param {Object} payload - Response data
   */
  submitSurvey: async (id, payload) => {
    return await apiClient.post(`/api/user/submit/${id}`, payload);
  },

  /**
   * Fetch quiz insights (admin)
   * @param {string} quizId - Quiz ID
   */
  fetchQuizInsights: async (quizId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/quiz-insights/${quizId}`,
    );
  },

  /**
   * Fetch quiz completion statistics
   * @param {string} quizId - Quiz ID
   */
  fetchQuizCompletionStats: async (quizId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/completion-stats/${quizId}`,
    );
  },

  /**
   * Fetch survey response statistics
   * @param {string} surveyId - Survey ID
   */
  fetchSurveyResponseStats: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/survey-insights/${surveyId}`,
    );
  },

  /**
   * Fetch survey activity statistics
   * @param {string} surveyId - Survey ID
   */
  fetchSurveyActivityStats: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/survey-activity-stats/${surveyId}`,
    );
  },

  /**
   * Fetch survey satisfaction insights
   * @param {string} surveyId - Survey ID
   */
  fetchSurveySatisfactionInsights: async (surveyId) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/satisfaction-insights/${surveyId}`,
    );
  },

  /**
   * Sync user data
   * @param {Object} userData - User data to sync
   */
  syncUser: async ({
    staffId,
    username,
    role,
    position,
    region,
    outlet,
    fcmToken,
  }) => {
    return await apiClient.post("/api/user/sync-user", {
      staffId,
      username,
      role,
      position,
      region,
      outlet,
      fcmToken,
    });
  },

  /**
   * Fetch segmentation data
   * @param {string} id - Survey ID
   */
  segmentation: async (id) => {
    return await apiClient.get(`/api/admin/quiz-survey/segmentation/${id}`);
  },

  /**
   * Get total staff invited count
   * @param {string} id - Survey ID
   */
  totalStaffInvited: async (id) => {
    return await apiClient.get(`/api/user/responses/staff-invited/${id}`);
  },

  /**
   * Get total responded users count
   * @param {string} id - Survey ID
   */
  totalRespondedUser: async (id) => {
    return await apiClient.get(`/api/user/responses/response-received/${id}`);
  },

  /**
   * Update survey configuration
   * @param {string} surveyId - Survey ID
   * @param {Array} userDataDisplayFields - Fields to display
   */
  updateSurveyConfig: async (surveyId, userDataDisplayFields) => {
    return await apiClient.put(`/api/admin/quiz-survey/${surveyId}`, {
      userDataDisplayFields,
    });
  },

  /**
   * Fetch least active users by region
   * @param {string} surveyId - Survey ID
   * @param {string} region - Region name
   */
  fetchLeastActiveUsersByRegion: (surveyId, region) => {
    return apiClient.get(
      `/api/user/survey-activity/least-active-users/region`,
      { params: { surveyId, region } },
    );
  },

  /* ===================== EXCEL EXPORT ===================== */

  /**
   * Download participation Excel for a single quiz/survey (ADMIN)
   * @param {string} quizSurveyId
   */
  downloadParticipationExcel: async (quizSurveyId) => {
    const response = await apiClient.get(
      `/api/admin/quiz-survey/participation/${quizSurveyId}/excel?_=${Date.now()}`,
      {
        responseType: "blob",
        headers: { "Cache-Control": "no-cache" },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let filename = "report.xlsx";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download overall quiz & survey participation Excel (ADMIN)
   */
  downloadOverallParticipationExcel: async () => {
    const response = await apiClient.get(
      `/api/admin/quiz-survey/participation/overall/excel?_=${Date.now()}`,
      { responseType: "blob" },
    );

    const contentDisposition = response.headers["content-disposition"];
    let filename = "Overall-Quiz-Survey-Report.xlsx";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /* ===================== OFFERS ===================== */

  /** Fetch all offers (ADMIN) */
  fetchAllOffers: async () => {
    return await apiClient.get("/api/user/offers");
  },

  /** Fetch active offers (USER) */
  fetchActiveOffers: async () => {
    return await apiClient.get("/api/user/offers/active");
  },

  /**
   * Fetch offers for a specific user (USER)
   * @param {string} userId
   * @param {string} role
   */
  fetchOffersForUser: async (userId, role) => {
    return await apiClient.get(`/api/user/offers/user/${userId}`);
  },

  /**
   * Create offer (ADMIN)
   * @param {Object} payload
   */
  createOffer: async (payload) => {
    return await apiClient.post("/api/user/offers", payload);
  },

  /**
   * Update offer (ADMIN)
   * @param {string} offerId
   * @param {Object} payload
   */
  updateOffer: async (offerId, payload) => {
    return await apiClient.put(`/api/user/offers/${offerId}`, payload);
  },

  /** Fetch ALL offers view report (ADMIN) */
  fetchAllOfferViewReport: async () => {
    return await apiClient.get("/api/admin/offers/view-report");
  },

  /**
   * Delete offer (ADMIN)
   * @param {string} offerId
   */
  deleteOffer: async (offerId) => {
    return await apiClient.delete(`/api/user/offers/${offerId}`);
  },

  /**
   * Fetch least active users by outlet
   * @param {string} surveyId - Survey ID
   * @param {string} outlet - Outlet name
   */
  fetchLeastActiveUsersByOutlet: (surveyId, outlet) => {
    return apiClient.get(
      `/api/user/survey-activity/least-active-users/outlet`,
      { params: { surveyId, outlet } },
    );
  },

  /* ===================== TRAINING & LEARNING ===================== */

  /**
   * Get Bunny TUS presigned upload signature (ADMIN)
   * @param {string} videoId
   */
  getTusSignature: async (videoId) => {
    return await apiClient.get("/api/user/training/bunny/tus-signature", {
      params: { videoId },
    });
  },

  createBunnyVideo: async (title) => {
    return await apiClient.post("/api/user/training/bunny/create-video", null, {
      params: { title },
    });
  },

  getBunnyUploadToken: async () => {
    return await apiClient.get("/api/user/training/bunny/upload-token");
  },

  /**
   * Get Bunny signed upload data (ADMIN)
   * @param {number} fileSize
   * @param {string} videoId
   */
  getBunnyUploadSignature: async (fileSize, videoId) => {
    return await apiClient.get("/api/user/training/bunny/upload-signature", {
      params: { fileSize, videoId },
    });
  },

  uploadDocument: async (formData) => {
    return await apiClient.post(
      "/api/user/training/bunny/upload-document",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  /**
   * Upload training material (ADMIN)
   * @param {Object} payload
   */
  uploadTraining: async (payload) => {
    return await apiClient.post("/api/user/training", payload);
  },

  /** Fetch all training materials (ADMIN) */
  fetchAllTrainings: async () => {
    return await apiClient.get("/api/user/training");
  },

  /**
   * Fetch learner engagement by training ID (ADMIN)
   * @param {string} trainingId
   */
  fetchTrainingEngagementById: async (trainingId) => {
    return await apiClient.get(`/api/user/training/${trainingId}/engagement`);
  },

  /**
   * Fetch single training by ID (ADMIN / USER)
   * @param {string} trainingId
   */
  fetchTrainingById: async (trainingId) => {
    return await apiClient.get(`/api/user/training/${trainingId}`);
  },

  /**
   * Fetch training materials by region
   * @param {string} region
   */
  fetchTrainingsByRegion: async (region) => {
    return await apiClient.get(`/api/user/training/region/${region}`);
  },

  /**
   * Assign training to users (ADMIN)
   * @param {Object} payload  { trainingId, userIds: [], dueDate }
   */
  assignTraining: async (payload) => {
    return await apiClient.post("/api/user/training/assign", payload);
  },

  /**
   * Fetch trainings assigned to a user (USER)
   * @param {string} userId
   */
  fetchUserTrainings: async (userId) => {
    return await apiClient.get(`/api/user/training/user/${userId}/details`);
  },

  /**
   * Update training progress (USER)
   * @param {Object} payload  { userId, trainingId, progress }
   */
  updateTrainingProgress: async (payload) => {
    return await apiClient.post("/api/user/training/progress", payload);
  },

  updateTraining: async (trainingId, payload) => {
    return await apiClient.put(`/api/user/training/${trainingId}`, payload);
  },

  deleteTraining: async (trainingId) => {
    return await apiClient.delete(`/api/user/training/${trainingId}`);
  },

  fetchVideoEngagement: async () => {
    return await apiClient.get("/api/user/training/engagement");
  },

  downloadVideoEngagementExcel: async (trainingId) => {
    return apiClient.get("/api/user/training/engagement/excel", {
      params: trainingId ? { trainingId } : {},
      responseType: "blob",
      headers: { "Cache-Control": "no-cache" },
    });
  },

  /** Fetch low scorers */
  fetchLowScorers: () => {
    return apiClient.get(`/api/user/responses/low-scorers`);
  },

  /**
   * Get single survey or quiz score by staff ID
   * @param {string} staffId
   * @param {string} quarter - Q1, Q2, Q3, Q4
   * @param {number} year
   */
  getSurveyScoreByStaffId: async (staffId, quarter, year) => {
    return await apiClient.get(
      `/api/admin/quiz-survey/user-score/by-staff/${staffId}`,
      { params: { quarter, year } },
    );
  },

  /**
   * Send notifications to users
   * @param {Object} payload - Notification data
   */
  notifyUsers: (payload) => {
    return apiClient.post(`/api/admin/announcements-with-targets`, payload);
  },

  /* ===================== NOTIFICATIONS ===================== */

  /**
   * Fetch all notifications for a user
   * @param {string} userId
   */
  fetchUserNotifications: async (userId) => {
    return await apiClient.get(`/api/user/notifications/${userId}`);
  },

  /**
   * Mark ALL notifications as read for a user
   * @param {string} userId
   */
  markAllNotificationsAsRead: async (userId) => {
    return await apiClient.put(`/api/user/notifications/read/${userId}`);
  },

  /**
   * Mark ONE notification as read
   * @param {string} notificationId
   */
  markNotificationAsRead: async (notificationId) => {
    return await apiClient.put(
      `/api/user/notifications/read-one/${notificationId}`,
    );
  },

  /**
   * Get unread notification count for badge
   * @param {string} userId
   */
  fetchUnreadNotificationCount: async (userId) => {
    return await apiClient.get(
      `/api/user/notifications/unread-count/${userId}`,
    );
  },

  /**
   * Check Bunny video processing status (ADMIN / USER)
   * @param {string} videoId - Bunny video GUID (NOT playback ID)
   */
  checkBunnyVideoStatus: async (videoId) => {
    return await apiClient.get(
      `/api/user/training/bunny/video-status/${videoId}`,
    );
  },

  /* ===================== OFFER REPORT ===================== */

  uploadOfferImage: async (formData) => {
    return await apiClient.post(
      "/api/user/offers/bunny/upload-image",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  /**
   * Mark offer as viewed (USER)
   * @param {string} offerId
   * @param {string} userId
   */
  markOfferAsViewed: async (offerId, userId) => {
    return await apiClient.put(`/api/user/offers/${offerId}/view`, null, {
      params: { userId },
    });
  },

  /**
   * Fetch offer view report (ADMIN)
   * @param {string} offerId
   */
  fetchOfferViewReport: async (offerId) => {
    return await apiClient.get(`/api/admin/offers/${offerId}/view-report`);
  },

  /** Download ALL offers view report Excel (ADMIN) */
  downloadAllOfferViewReportExcel: async () => {
    return await apiClient.get("/api/admin/offers/view-report/excel", {
      responseType: "blob",
      headers: { "Cache-Control": "no-cache" },
    });
  },

  // 📡 SSE — expose the singleton on the api object for convenience
  /**
   * Connect SSE for real-time mandatory quiz pushes.
   * Mirrors Flutter SseService.connect().
   * @param {string} username
   * @param {string} token
   * @param {Function} onMandatoryPush
   * @param {Function} [onAuthError]
   */
  connectSse: ({ username, token, onMandatoryPush, onAuthError }) => {
    sseService.connect({ username, token, onMandatoryPush, onAuthError });
  },

  /** Disconnect SSE. Mirrors Flutter SseService.disconnect(). */
  disconnectSse: () => sseService.disconnect(),

  /** @returns {boolean} */
  isSseConnected: () => sseService.isConnected,

  /**
   * Rotate the Bearer token without a full reconnect.
   * @param {string} newToken
   */
  updateSseToken: (newToken) => sseService.updateToken(newToken),
};

// Export both the api object and the client for advanced usage
export { apiClient };
export default api;
